import { useState, useEffect, useMemo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { MODO_CREDITO_UNIFICADO, MODO_SEM_FASE_ANALISE } from '../config';
import ImagemProduto from '../components/ImagemProduto';
import ModalDetalhePedido from '../components/ModalDetalhePedido';
import AnimatedNumber from '../components/AnimatedNumber';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';

const brl = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const brlAxis = v => {
  if (Math.abs(v) >= 1000) return `R$${(v / 1000).toFixed(0)}k`;
  return `R$${v.toFixed(0)}`;
};

const mesFmt = m => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const nomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${nomes[parseInt(mo) - 1]}/${y.slice(2)}`;
};

// Primeiro nome capitalizado (tira sufixos como "-ARQUITETO", "(ARQUITETA)")
const primeiroNome = (nome) => {
  const limpo = String(nome || '').replace(/\s*[-(].*$/, '').trim();
  const p = limpo.split(/\s+/)[0] || '';
  return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : '';
};

// Período inicial padrão: últimos 12 meses
function periodoInicial() {
  const hoje = new Date();
  const f = (d) => d.toISOString().slice(0, 10);
  const inicio = new Date(); inicio.setDate(inicio.getDate() - 365);
  return { inicio: f(inicio), fim: f(hoje) };
}

// Navegador de meses (estilo financas-pessoais) — fica abaixo do filtro de período
// e permite saltar entre meses específicos rápido.
const NOMES_MES_CURTOS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

// Detecta se o período corresponde a um mês inteiro (pra destacar o mês ativo)
function detectarMes(inicio, fim) {
  if (!inicio || !fim) return { ano: new Date().getFullYear(), mes: null };
  const [aI, mI, dI] = inicio.split('-').map(Number);
  const [aF, mF, dF] = fim.split('-').map(Number);
  const ultDia = new Date(aI, mI, 0).getDate();
  const ehMesInteiro = aI === aF && mI === mF && dI === 1 && dF === ultDia;
  return { ano: aI, mes: ehMesInteiro ? mI : null };
}

function NavegadorMes({ inicio, fim, aoMudar }) {
  const { ano, mes: mesAtivo } = detectarMes(inicio, fim);
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1;

  function selecionarMes(a, m) {
    const ini = `${a}-${String(m).padStart(2, '0')}-01`;
    const ult = new Date(a, m, 0).getDate();
    const fimM = `${a}-${String(m).padStart(2, '0')}-${String(ult).padStart(2, '0')}`;
    aoMudar(ini, fimM);
  }
  function mudarAno(delta) {
    selecionarMes(ano + delta, mesAtivo || 1);
  }

  return (
    <div
      className="d-flex align-items-center gap-2 mb-4 pb-2 navegador-mes"
      style={{
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        borderBottom: '1px solid var(--borda)',
        flexWrap: 'nowrap',
      }}
    >
      <button onClick={() => mudarAno(-1)} title="Ano anterior" aria-label="Ano anterior" style={navBtn}>
        <i className="bi bi-chevron-left" />
      </button>
      <span className="fw-bold" style={{
        minWidth: 52, textAlign: 'center', fontSize: '0.95rem',
        color: 'var(--primary-dark)',
      }}>{ano}</span>
      <button onClick={() => mudarAno(1)} title="Próximo ano" aria-label="Próximo ano" style={navBtn}>
        <i className="bi bi-chevron-right" />
      </button>
      <div style={{ width: 1, height: 24, background: 'var(--borda)', margin: '0 4px', flexShrink: 0 }} />
      {NOMES_MES_CURTOS.map((m, i) => {
        const num = i + 1;
        const ativo = num === mesAtivo;
        const ehHoje = ano === anoAtual && num === mesAtual;
        return (
          <button
            key={i}
            className="mes-btn"
            onClick={() => selecionarMes(ano, num)}
            title={ehHoje ? 'Mês atual' : ''}
            style={{
              padding: '7px 14px',
              border: 'none',
              background: ativo ? 'var(--primary)' : 'transparent',
              color: ativo ? '#fff' : (ehHoje ? 'var(--primary-dark)' : 'var(--text-suave)'),
              borderRadius: 999,
              fontWeight: ativo || ehHoje ? 600 : 500,
              fontSize: '0.88rem',
              cursor: 'pointer',
              transition: 'all 0.18s',
              position: 'relative',
              minHeight: 34,
              flexShrink: 0,
              boxShadow: ativo ? '0 5px 14px rgba(15,59,31,0.28)' : 'none',
            }}
          >
            {m}
            {ehHoje && !ativo && (
              <span style={{
                position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)',
                width: 4, height: 4, borderRadius: '50%', background: 'var(--primary)',
              }} />
            )}
          </button>
        );
      })}

      {/* Inputs De/Até alinhados à direita */}
      <div className="d-flex align-items-center gap-1 ms-auto" style={{ flexShrink: 0 }}>
        <span className="text-muted" style={{ fontSize: '0.78rem' }}>De</span>
        <input
          type="date"
          value={inicio || ''}
          max={fim || undefined}
          onChange={e => aoMudar(e.target.value, fim)}
          title="Data inicial"
          style={inputData}
        />
        <span className="text-muted" style={{ fontSize: '0.78rem' }}>até</span>
        <input
          type="date"
          value={fim || ''}
          min={inicio || undefined}
          onChange={e => aoMudar(inicio, e.target.value)}
          title="Data final"
          style={inputData}
        />
      </div>
    </div>
  );
}

const navBtn = {
  border: '1px solid var(--borda)',
  background: 'var(--surface)',
  borderRadius: 10,
  padding: '5px 11px',
  cursor: 'pointer',
  minHeight: 34,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: 'var(--text-suave)',
};

const inputData = {
  border: '1px solid var(--borda)',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: '0.85rem',
  minHeight: 32,
  background: '#fff',
};

function TooltipCustom({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
      padding: '10px 14px', fontSize: '0.85rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div className="fw-semibold mb-1" style={{ color: '#333' }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{brl(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

// Cards de fase. Quando MODO_CREDITO_UNIFICADO, "Crédito Interno" some.
// Quando MODO_SEM_FASE_ANALISE, "Em Análise" também some — tudo cai em
// "Disponível para Saque".
function montarCards() {
  const cards = [
    { key: 'disponivel_saque', label: 'Disponível para Saque', cor: 'var(--primary)' },
  ];
  if (!MODO_CREDITO_UNIFICADO) {
    cards.push({ key: 'credito_interno', label: 'Crédito Interno', cor: 'var(--accent)' });
  }
  if (!MODO_SEM_FASE_ANALISE) {
    cards.push({ key: 'em_analise', label: 'Em Análise', cor: '#888' });
  }
  cards.push({ key: 'pago', label: 'Já Recebido', cor: 'var(--success)' });
  return cards;
}
const CARDS = montarCards();

const ZERO = { disponivel_saque: 0, credito_interno: 0, em_analise: 0, pago: 0, total: 0 };

// Agrega fases arquivadas em disponivel_saque pra exibição. O SQL continua
// segregando — basta trocar as flags pra a UI voltar a separar.
function unificarSeNecessario(linha) {
  if (!MODO_CREDITO_UNIFICADO && !MODO_SEM_FASE_ANALISE) return linha;
  const credInt = MODO_CREDITO_UNIFICADO ? (linha.credito_interno || 0) : 0;
  const emAnal  = MODO_SEM_FASE_ANALISE  ? (linha.em_analise || 0)      : 0;
  return {
    ...linha,
    disponivel_saque: (linha.disponivel_saque || 0) + credInt + emAnal,
    credito_interno: MODO_CREDITO_UNIFICADO ? 0 : (linha.credito_interno || 0),
    em_analise:      MODO_SEM_FASE_ANALISE  ? 0 : (linha.em_analise || 0),
  };
}

// Componente do delta % no card (#8)
function DeltaIndicador({ atual, anterior }) {
  if (!anterior) return null;
  if (Math.abs(anterior) < 0.01) return null;
  const pct = ((atual - anterior) / Math.abs(anterior)) * 100;
  if (!isFinite(pct) || Math.abs(pct) < 0.5) return null;
  const positivo = pct >= 0;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      marginLeft: 8, padding: '2px 6px', borderRadius: 12,
      fontSize: '0.7rem', fontWeight: 700,
      background: positivo ? '#e6f5ea' : '#fdecea',
      color: positivo ? '#1f6a2d' : '#b53d3d',
    }} title={`Comparando com ${brl(anterior)} no mês anterior`}>
      <i className={`bi ${positivo ? 'bi-arrow-up-short' : 'bi-arrow-down-short'}`} />
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

export default function Dashboard() {
  const { user, temPermissao } = useAuth();
  const isArquiteto = user?.tipo === 'arquiteto';
  const podeVerComissoes = isArquiteto || temPermissao('comissoes_listar');

  // Período padrão: últimos 12 meses. Mudado via inputs De/Até ou navegador de meses.
  const [periodo, setPeriodo] = useState(periodoInicial);
  const filtroAtivo = periodo;

  const [mensal, setMensal] = useState([]);
  const [porArquiteto, setPorArquiteto] = useState([]);
  const [carregando, setCarregando] = useState(true);

  // Novas seções (mai/2026) — só pra arquiteto
  // (top-clientes do backend continua disponível pra uso futuro; removemos da UI por
  //  redundância com "Maiores comissões", que já mostra nome do cliente do pedido)
  const [maioresComissoes, setMaioresComissoes] = useState([]);
  const [topProdutos, setTopProdutos] = useState([]);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);

  const carregar = useCallback(() => {
    if (!podeVerComissoes) { setCarregando(false); return; }
    setCarregando(true);

    const qs = new URLSearchParams();
    if (filtroAtivo.inicio) qs.set('dataInicio', filtroAtivo.inicio);
    if (filtroAtivo.fim)    qs.set('dataFim', filtroAtivo.fim);
    const q = qs.toString() ? `?${qs}` : '';

    if (isArquiteto) {
      Promise.all([
        api.get(`/comissoes/sintetica${q}`),
        api.get(`/comissoes/maiores-comissoes${q ? q + '&' : '?'}limit=5`),
        api.get(`/comissoes/top-produtos${q ? q + '&' : '?'}limit=6`),
      ])
        .then(([sint, mc, tp]) => {
          setMensal(sint.data.dados || []);
          setMaioresComissoes(mc.data || []);
          setTopProdutos(tp.data || []);
        })
        .catch(() => { setMensal([]); setMaioresComissoes([]); setTopProdutos([]); })
        .finally(() => setCarregando(false));
    } else {
      const qMensal = qs.toString() ? `${q}&agrupamento=mensal` : '?agrupamento=mensal';
      Promise.all([
        api.get(`/comissoes/sintetica${q}`),
        api.get(`/comissoes/sintetica${qMensal}`),
      ])
        .then(([arq, men]) => {
          setPorArquiteto(
            [...(arq.data.dados || [])].sort((a, b) => b.total - a.total).slice(0, 10)
          );
          setMensal(men.data.dados || []);
        })
        .catch(() => { setMensal([]); setPorArquiteto([]); })
        .finally(() => setCarregando(false));
    }
  }, [isArquiteto, podeVerComissoes, filtroAtivo.inicio, filtroAtivo.fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const totais = useMemo(() =>
    mensal.reduce((acc, raw) => {
      const r = unificarSeNecessario(raw);
      return {
        disponivel_saque: acc.disponivel_saque + (r.disponivel_saque || 0),
        credito_interno:  acc.credito_interno  + (r.credito_interno  || 0),
        em_analise:       acc.em_analise       + (r.em_analise       || 0),
        pago:             acc.pago             + (r.pago             || 0),
        total:            acc.total            + (r.total            || 0),
      };
    }, { ...ZERO }),
  [mensal]);

  // Delta % nos cards — pega último mês com dado e compara com o anterior.
  // `mensal` vem em ordem decrescente (mais recente primeiro).
  const deltas = useMemo(() => {
    if (mensal.length < 2) return null;
    const atual = unificarSeNecessario(mensal[0]);
    const anterior = unificarSeNecessario(mensal[1]);
    return {
      disponivel_saque: { atual: atual.disponivel_saque, anterior: anterior.disponivel_saque },
      credito_interno:  { atual: atual.credito_interno,  anterior: anterior.credito_interno },
      em_analise:       { atual: atual.em_analise,       anterior: anterior.em_analise },
      pago:             { atual: atual.pago,             anterior: anterior.pago },
    };
  }, [mensal]);

  // Gráfico acompanha o filtro: backend já retorna só os meses do período
  // (passamos dataInicio/dataFim na query da sintética). Limita visualmente a 24
  // meses pra não estourar largura em "Tudo" / "12 meses".
  const dadosMensal = useMemo(() =>
    [...mensal].slice(0, 24).reverse().map(raw => {
      const r = unificarSeNecessario(raw);
      return { ...r, label: mesFmt(r.mes) };
    }),
  [mensal]);

  const dadosArquiteto = useMemo(() =>
    porArquiteto.map(raw => {
      const r = unificarSeNecessario(raw);
      return {
        ...r,
        label: r.nome.length > 22 ? r.nome.slice(0, 22) + '…' : r.nome,
      };
    }),
  [porArquiteto]);

  // (#11) Empty state — arquiteto novo sem comissão alguma
  const semDados = !carregando && mensal.length === 0;

  return (
    <div>
      <div className="dash-header">
        <div className="dash-eyebrow">{isArquiteto ? 'PORTAL DO ARQUITETO' : 'VISÃO GERAL'}</div>
        <h1 className="dash-titulo">
          {isArquiteto
            ? <>Olá, <span>{primeiroNome(user?.nome)}</span> 👋</>
            : 'Dashboard'}
        </h1>
        <p className="dash-sub">
          {isArquiteto ? 'Aqui está o resumo das suas comissões por indicação.' : 'Comissões por indicação'}
        </p>
      </div>

      {/* Navegador de meses + inputs De/Até na mesma linha */}
      {podeVerComissoes && (
        <NavegadorMes
          inicio={periodo.inicio}
          fim={periodo.fim}
          aoMudar={(ini, fim) => setPeriodo({ inicio: ini, fim: fim })}
        />
      )}

      {carregando ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
        </div>
      ) : !podeVerComissoes ? (
        <div className="card p-4 text-center text-muted">
          <i className="bi bi-lock" style={{ fontSize: '2rem', color: '#ccc' }}></i>
          <p className="mt-2 mb-0" style={{ fontSize: '0.9rem' }}>Sem permissão para visualizar comissões</p>
        </div>
      ) : semDados ? (
        // (#11) Empty state pro arquiteto novo
        <div className="card p-5 text-center" style={{ borderTop: '4px solid var(--accent)' }}>
          <i className="bi bi-stars" style={{ fontSize: '2.5rem', color: 'var(--accent)' }}></i>
          <h5 className="fw-bold mt-3 mb-2" style={{ color: 'var(--primary-dark)' }}>
            {isArquiteto ? `Bem-vindo, ${user?.nome}!` : 'Sem registros no período'}
          </h5>
          {isArquiteto ? (
            <>
              <p className="text-muted mx-auto mb-3" style={{ maxWidth: 520, fontSize: '0.92rem' }}>
                Você ainda não tem comissões registradas. Toda venda em que você for indicado(a)
                gera uma comissão automaticamente no Centrão — e ela aparece aqui assim que sai da
                fase de validação.
              </p>
              <div className="row g-3 mx-auto" style={{ maxWidth: 560 }}>
                <div className="col-md-6">
                  <div style={{ background: '#f5f9f5', borderRadius: 10, padding: 14 }}>
                    <i className="bi bi-1-circle-fill" style={{ color: 'var(--primary)', fontSize: '1.2rem' }} />
                    <div className="fw-semibold mt-2" style={{ fontSize: '0.88rem' }}>Indicação</div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>O cliente compra no Centrão indicado por você.</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div style={{ background: '#f5f9f5', borderRadius: 10, padding: 14 }}>
                    <i className="bi bi-2-circle-fill" style={{ color: 'var(--primary)', fontSize: '1.2rem' }} />
                    <div className="fw-semibold mt-2" style={{ fontSize: '0.88rem' }}>Saque</div>
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>Comissão disponível para sacar via PIX.</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-muted mb-0" style={{ fontSize: '0.92rem' }}>
              Nenhuma comissão encontrada para o período selecionado.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* KPIs — card herói (disponível) + secundários, com números que contam */}
          <div className="kpi-grid mb-4">
            <div className="kpi-hero">
              <div className="kpi-hero-glow" />
              <div className="kpi-hero-grid" />
              <div className="kpi-hero-label">
                <i className="bi bi-wallet2 me-2" />Disponível para saque
              </div>
              <div className="kpi-hero-valor">
                <AnimatedNumber value={totais.disponivel_saque || 0} format={brl} />
              </div>
              <div className="kpi-hero-row">
                {deltas?.disponivel_saque && (
                  <DeltaIndicador atual={deltas.disponivel_saque.atual} anterior={deltas.disponivel_saque.anterior} />
                )}
                {isArquiteto && totais.disponivel_saque > 0 && (
                  <NavLink to="/saques" className="kpi-hero-cta">
                    <i className="bi bi-bank2" /> Solicitar saque <i className="bi bi-arrow-right" />
                  </NavLink>
                )}
              </div>
            </div>

            <div className="kpi-secs">
              {CARDS.filter(c => c.key !== 'disponivel_saque').map((c, i) => (
                <div key={c.key} className="kpi-sec" style={{ '--kpi-cor': c.cor, animationDelay: `${0.1 + i * 0.08}s` }}>
                  <div className="kpi-sec-label">{c.label}</div>
                  <div className="kpi-sec-valor">
                    <AnimatedNumber value={totais[c.key] || 0} format={brl} />
                  </div>
                  {deltas?.[c.key] && (
                    <div className="kpi-sec-delta">
                      <DeltaIndicador atual={deltas[c.key].atual} anterior={deltas[c.key].anterior} />
                      <span>vs. mês anterior</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* SEÇÕES NOVAS (arquiteto) — top comissões, clientes e produtos */}
          {isArquiteto && (
            <div className="row g-4 mb-4">
              {/* Maiores comissões — ranking visual com medalha + barras proporcionais */}
              {maioresComissoes.length > 0 && (
                <div className="col-12">
                  <div className="card p-4 h-100">
                    <div className="d-flex align-items-center mb-3">
                      <i className="bi bi-trophy-fill me-2" style={{ color: 'var(--accent)' }}></i>
                      <h6 className="fw-bold mb-0" style={{ color: 'var(--primary-dark)' }}>Suas maiores comissões</h6>
                      <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>no período</span>
                    </div>
                    <div className="rank-list">
                      {maioresComissoes.map((c, i) => {
                        const max = maioresComissoes[0]?.comissao || 1;
                        const pct = Math.max(6, (c.comissao / max) * 100);
                        return (
                          <div
                            key={c.codigo}
                            onClick={() => setPedidoSelecionado(c.numped)}
                            className="rank-item"
                            role="button" tabIndex={0}
                            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setPedidoSelecionado(c.numped)}
                          >
                            <div className={`rank-badge rank-${i + 1}`}>{i + 1}</div>
                            <div className="rank-body">
                              <div className="rank-top">
                                <span className="rank-nome text-truncate">{c.razaocli || 'Cliente'}</span>
                                <span className="rank-valor"><AnimatedNumber value={c.comissao} format={brl} /></span>
                              </div>
                              <div className="rank-sub">Pedido #{c.numped} · {new Date(c.emissao).toLocaleDateString('pt-BR')}</div>
                              <div className="rank-track"><div className="rank-fill" style={{ width: `${pct}%`, animationDelay: `${0.15 + i * 0.1}s` }} /></div>
                            </div>
                            <i className="bi bi-chevron-right rank-chev" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Top produtos — cards com hover dinâmico */}
              {topProdutos.length > 0 && (
                <div className="col-12">
                  <div className="card p-4">
                    <div className="d-flex align-items-center mb-3">
                      <i className="bi bi-box-seam-fill me-2" style={{ color: 'var(--primary)' }}></i>
                      <h6 className="fw-bold mb-0" style={{ color: 'var(--primary-dark)' }}>Produtos que você mais vendeu</h6>
                      <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>no período</span>
                    </div>
                    <div className="prod-grid">
                      {topProdutos.map((p, i) => (
                        <div key={p.codpro} className="prod-card" style={{ animationDelay: `${i * 0.06}s` }}>
                          <div className="prod-img"><ImagemProduto codpro={p.codpro} tamanho="lg" clicavel alt={p.descr} /></div>
                          <div className="prod-desc">{p.descr}</div>
                          <div className="prod-meta">
                            <span className="prod-qtd">{p.quant_total.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} un.</span>
                            <span className="prod-valor">{brl(p.valor_vendido)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gráficos — arquiteto */}
          {isArquiteto && dadosMensal.length > 0 && (
            <div className="card p-4">
              <h6 className="fw-semibold mb-4" style={{ color: 'var(--primary)', fontSize: '0.95rem' }}>
                <i className="bi bi-bar-chart me-2"></i>Comissões por Mês
                <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.8rem' }}>
                  ({dadosMensal.length} {dadosMensal.length === 1 ? 'mês' : 'meses'})
                </span>
              </h6>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={dadosMensal} barCategoryGap="30%">
                  <defs>
                    <linearGradient id="gDisp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#3FB05A" /><stop offset="1" stopColor="#1F6A2D" />
                    </linearGradient>
                    <linearGradient id="gPago" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#7FA98A" /><stop offset="1" stopColor="#557E64" />
                    </linearGradient>
                    <linearGradient id="gCred" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#F5A623" /><stop offset="1" stopColor="#E2901A" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2ee" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6F7A70' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={brlAxis} tick={{ fontSize: 12, fill: '#6F7A70' }} axisLine={false} tickLine={false} width={72} />
                  <Tooltip content={<TooltipCustom />} cursor={{ fill: 'rgba(31,106,45,0.05)' }} />
                  <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: 12 }} />
                  <Bar dataKey="pago"             name="Já Recebido"           stackId="a" fill="url(#gPago)" />
                  <Bar dataKey="disponivel_saque" name="Disponível para Saque" stackId="a" fill="url(#gDisp)" radius={MODO_SEM_FASE_ANALISE ? [6,6,0,0] : undefined} />
                  {!MODO_CREDITO_UNIFICADO && (
                    <Bar dataKey="credito_interno" name="Crédito Interno" stackId="a" fill="url(#gCred)" />
                  )}
                  {!MODO_SEM_FASE_ANALISE && (
                    <Bar dataKey="em_analise"     name="Em Análise"       stackId="a" fill="#c4ccc6" radius={[6,6,0,0]} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Gráficos — admin */}
          {!isArquiteto && (
            <div className="row g-4">
              <div className="col-12 col-xl-7">
                <div className="card p-4">
                  <h6 className="fw-semibold mb-4" style={{ color: 'var(--primary)', fontSize: '0.95rem' }}>
                    <i className="bi bi-trophy me-2"></i>Top Arquitetos — Total de Comissões
                  </h6>
                  {dadosArquiteto.length === 0 ? (
                    <p className="text-muted text-center py-4">Nenhuma comissão registrada no período</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(240, dadosArquiteto.length * 42)}>
                      <BarChart layout="vertical" data={dadosArquiteto} barCategoryGap="28%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tickFormatter={brlAxis} tick={{ fontSize: 11, fill: '#666' }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 12, fill: '#444' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<TooltipCustom />} cursor={{ fill: 'rgba(46,152,70,0.05)' }} />
                        <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: 12 }} />
                        <Bar dataKey="pago"             name="Já Recebido"           stackId="a" fill="#4F6F52" />
                        <Bar dataKey="disponivel_saque" name="Disponível para Saque" stackId="a" fill="#1F6A2D" radius={MODO_SEM_FASE_ANALISE ? [0,4,4,0] : undefined} />
                        {!MODO_CREDITO_UNIFICADO && (
                          <Bar dataKey="credito_interno" name="Crédito Interno" stackId="a" fill="#E2901A" />
                        )}
                        {!MODO_SEM_FASE_ANALISE && (
                          <Bar dataKey="em_analise"     name="Em Análise"       stackId="a" fill="#aaa" radius={[0,4,4,0]} />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="col-12 col-xl-5">
                <div className="card p-4">
                  <h6 className="fw-semibold mb-4" style={{ color: 'var(--primary)', fontSize: '0.95rem' }}>
                    <i className="bi bi-graph-up me-2"></i>Evolução Mensal
                    <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.8rem' }}>
                      ({dadosMensal.length} {dadosMensal.length === 1 ? 'mês' : 'meses'})
                    </span>
                  </h6>
                  {dadosMensal.length === 0 ? (
                    <p className="text-muted text-center py-4">Nenhum registro</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={dadosMensal}>
                        <defs>
                          <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#1F6A2D" stopOpacity={0.18} />
                            <stop offset="95%" stopColor="#1F6A2D" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradDisponivel" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4F6F52" stopOpacity={0.12} />
                            <stop offset="95%" stopColor="#4F6F52" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#666' }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={brlAxis} tick={{ fontSize: 11, fill: '#666' }} axisLine={false} tickLine={false} width={64} />
                        <Tooltip content={<TooltipCustom />} />
                        <Legend wrapperStyle={{ fontSize: '0.85rem', paddingTop: 12 }} />
                        <Area type="monotone" dataKey="total" name="Total"         stroke="#1F6A2D" fill="url(#gradTotal)"     strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="pago"  name="Já Recebido"   stroke="#4F6F52" fill="url(#gradDisponivel)" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de detalhe — disparado por click nas "maiores comissões" */}
      <ModalDetalhePedido
        numped={pedidoSelecionado}
        aberto={!!pedidoSelecionado}
        aoFechar={() => setPedidoSelecionado(null)}
      />
    </div>
  );
}
